import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { ErrorReasons } from '@shared/constants/errors.constants'
import {
  CreateCompanyForm,
  createCompanyFormSchema
} from '@shared/dto/companies.dto'
import {
  CompanyType,
  CompanyUsersRelation
} from '@shared/types/companies.types'
import { DefaultRole } from '@shared/types/roles.types'
import { useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { TextInput } from '../../components/forms/TextInput'
import UploadFileInput from '../../components/forms/UploadFileInput'
import SubHeaderProfile from '../../components/SubHeaderProfile'
import { useCreateCompanyMutation } from '../../hooks/useMutations/useCompaniesMutations'

const errorMessages = {
  invalid_email: {
    title: 'create_company.errors.invalid_email.title',
    description: 'create_company.errors.invalid_email.description'
  },
  invalid_domain: {
    title: 'create_company.errors.invalid_domain.title',
    description: 'create_company.errors.invalid_domain.description'
  },
  unexpected: {
    title: 'error.unexpected.title',
    description: 'error.unexpected.description'
  },
  user_already_exists: {
    title: 'create_company.errors.user_already_exists.title',
    description: 'create_company.errors.user_already_exists.description'
  }
}

const defaultValues = {
  company: {
    name: '',
    tin: '',
    logoUri: undefined,
    type: CompanyType.CONSTRUCTOR
  },
  users: Array(3).fill({ role: '', relation: '', email: '' })
}

export default function CompaniesCreate() {
  const [errorType, setErrorType] = useState('')
  const [isAuthorized, setIsAuthorized] = useState(false)

  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid }
  } = useForm<CreateCompanyForm>({
    defaultValues,
    resolver: standardSchemaResolver(createCompanyFormSchema),
    mode: 'onBlur'
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'users'
  })

  const createCompanyMutation = useCreateCompanyMutation()

  const usersErrors = Array.isArray(errors?.users) ? errors.users : []
  const hasInvalidEmail = usersErrors.some(
    e => e?.email?.message === 'Invalid input'
  )
  const hasInvalidDomain = usersErrors.some(
    e => e?.email?.message === 'Invalid email domain'
  )

  const onSubmit = async (data: CreateCompanyForm) => {
    setErrorType('')

    // Removing empty users
    const cleanedData = {
      ...data,
      users: data.users?.filter(
        user => user.role && user.relation && user.email
      )
    }

    createCompanyMutation.mutate(cleanedData, {
      onSuccess: () => {
        navigate('/companies')
      },
      onError: (error: any) => {
        if (
          error.response.status === 409 &&
          error.response.data.data.reason ===
            ErrorReasons.USER_ALREADY_IN_COMPANY
        ) {
          setErrorType('user_already_exists')
          return
        }

        setErrorType('unexpected')
      }
    })
  }

  return (
    <>
      <SubHeaderProfile
        title={t('common.account')}
        subtitle={t('create_company.create')}
        isDirty={false}
      />

      <Container maxWidth='xl' disableGutters>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            width: '61.5rem',
            mx: 'auto',
            padding: '2rem'
          }}
        >
          <Typography
            variant='h5'
            sx={{ padding: '1rem 1.25rem' }}
            fontWeight='bold'
          >
            {t('create_company.create')}
          </Typography>

          <Paper
            sx={{
              padding: '1.5rem',
              borderRadius: '0.25rem',
              background: '#232629'
            }}
          >
            <Stack spacing='1.5rem'>
              <Stack spacing='.5rem'>
                <Stack direction='row' justifyContent='space-between'>
                  <Typography variant='h6' sx={{ lineHeight: '2rem' }}>
                    {t('create_company.details.title')}
                  </Typography>
                  <Typography variant='body2'>
                    {t('create_company.required')}
                  </Typography>
                </Stack>
                <Typography variant='body1'>
                  {t('create_company.details.subtitle')}
                </Typography>
              </Stack>
              <TextInput
                name='company.name'
                register={register}
                errors={errors}
                label={t('create_company.details.company_name')}
                required
              />
              <TextInput
                name='company.tin'
                register={register}
                errors={errors}
                label={t('create_company.details.company_tin')}
                required
              />
              <Stack
                spacing='.5rem'
                flexDirection='column'
                alignItems='flex-start'
              >
                <Typography variant='body2' fontWeight='bold'>
                  {t('create_company.details.logo_label')}
                </Typography>
                <UploadFileInput
                  label={t('create_company.details.upload_logo')}
                  name='company.logoUri'
                  setValue={setValue}
                  acceptedFormats='image/png, image/jpg, image/jpeg'
                  acceptedFormatsLabel={t(
                    'create_company.details.logo_formats'
                  )}
                />
              </Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={() => setIsAuthorized(!isAuthorized)}
                    checked={isAuthorized}
                  />
                }
                label={
                  <Typography component='span'>
                    {t('create_company.details.authorized')}
                    <Box component='span' sx={{ marginLeft: '.25rem' }}>
                      <Link
                        to='/terms'
                        target='_blank'
                        rel='noopener'
                        style={{ color: '#00E5FF' }}
                      >
                        {t('common.see_more')}
                      </Link>
                    </Box>
                  </Typography>
                }
              />
            </Stack>
          </Paper>

          <Paper
            sx={{
              padding: '1.5rem',
              borderRadius: '0.25rem',
              background: '#232629'
            }}
          >
            <Stack spacing='1.5rem'>
              <Stack spacing='.5rem'>
                <Stack direction='row' justifyContent='space-between'>
                  <Typography variant='h6' fontWeight='bold'>
                    {t('create_company.type.title')}
                  </Typography>
                  <Typography variant='body2'>
                    {t('create_company.required')}
                  </Typography>
                </Stack>
                <Typography variant='body1'>
                  {t('create_company.type.subtitle')}
                </Typography>
              </Stack>

              <Controller
                name='company.type'
                control={control}
                render={({ field }) => (
                  <FormControl>
                    <RadioGroup
                      {...field}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem'
                      }}
                    >
                      {[CompanyType.CONSTRUCTOR, CompanyType.PROVIDER].map(
                        type => (
                          <Box
                            key={type}
                            sx={{
                              border: '1px solid',
                              borderColor:
                                field.value === type
                                  ? 'primary.main'
                                  : 'divider',
                              background: '#3A3D40',
                              padding: '1rem'
                            }}
                          >
                            <Box
                              display='flex'
                              justifyContent='space-between'
                              alignItems='center'
                            >
                              <Box>
                                <Typography variant='body1'>
                                  {t(
                                    `create_company.type.${type.toLowerCase()}_title`
                                  )}
                                </Typography>
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                >
                                  {t(
                                    `create_company.type.${type.toLowerCase()}_description`
                                  )}
                                </Typography>
                              </Box>

                              <Radio
                                value={type}
                                checked={field.value === type}
                                onChange={field.onChange}
                              />
                            </Box>
                          </Box>
                        )
                      )}
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </Stack>
          </Paper>

          <Accordion sx={{ background: '#232629' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='h6' fontWeight='bold'>
                {t('create_company.members.title')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing='1rem'>
                <Table
                  sx={{
                    '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root':
                      { borderBottom: 'none' }
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t('create_company.members.user_role.head')}
                      </TableCell>
                      <TableCell>
                        {t('create_company.members.relation.head')}
                      </TableCell>
                      <TableCell>{t('create_company.members.email')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((field, index) => (
                      <TableRow key={field.id}>
                        <TableCell>
                          <Controller
                            control={control}
                            name={`users.${index}.role`}
                            render={({ field }) => (
                              <Select
                                sx={{
                                  background: '#3A3D40',
                                  borderRadius: '0.25rem',
                                  '& .MuiSelect-select': {
                                    color: 'text.secondary'
                                  },
                                  '& .MuiSelect-icon': {
                                    color: 'text.secondary'
                                  }
                                }}
                                {...field}
                                fullWidth
                                displayEmpty
                                renderValue={selected =>
                                  selected
                                    ? t(
                                        `create_company.members.user_role.${selected.toLowerCase()}`
                                      )
                                    : t(
                                        'create_company.members.user_role.choose'
                                      )
                                }
                              >
                                <MenuItem value={DefaultRole.OWNER}>
                                  {t('create_company.members.user_role.owner')}
                                </MenuItem>
                                <MenuItem value={DefaultRole.ADMIN}>
                                  {t('create_company.members.user_role.admin')}
                                </MenuItem>
                                <MenuItem value={DefaultRole.MEMBER}>
                                  {t('create_company.members.user_role.member')}
                                </MenuItem>
                              </Select>
                            )}
                          />
                        </TableCell>

                        <TableCell>
                          <Controller
                            control={control}
                            name={`users.${index}.relation`}
                            render={({ field }) => (
                              <Select
                                {...field}
                                sx={{
                                  background: '#3A3D40',
                                  borderRadius: '0.25rem',
                                  '& .MuiSelect-select': {
                                    color: 'text.secondary'
                                  },
                                  '& .MuiSelect-icon': {
                                    color: '#FFFFFF'
                                  }
                                }}
                                fullWidth
                                displayEmpty
                                renderValue={selected =>
                                  selected
                                    ? t(
                                        `create_company.members.relation.${selected.toLowerCase()}`
                                      )
                                    : t(
                                        'create_company.members.relation.choose'
                                      )
                                }
                              >
                                <MenuItem value={CompanyUsersRelation.EMPLOYEE}>
                                  {t(
                                    'create_company.members.relation.employee'
                                  )}
                                </MenuItem>
                                <MenuItem value={CompanyUsersRelation.CLIENT}>
                                  {t('create_company.members.relation.client')}
                                </MenuItem>
                                <MenuItem value={CompanyUsersRelation.PROVIDER}>
                                  {t(
                                    'create_company.members.relation.provider'
                                  )}
                                </MenuItem>
                              </Select>
                            )}
                          />
                        </TableCell>

                        <TableCell>
                          <Stack
                            direction='row'
                            justifyContent='space-between'
                            alignItems='flex-start'
                            sx={{ width: '100%' }}
                          >
                            <TextField
                              {...register(`users.${index}.email`)}
                              fullWidth
                              label={t('create_company.members.email')}
                              variant='outlined'
                              size='medium'
                              error={!!errors.users?.[index]?.email}
                              sx={{
                                background: '#3A3D40',
                                borderRadius: '0.25rem'
                              }}
                            />

                            <Button
                              onClick={() => remove(index)}
                              variant='text'
                              size='small'
                              sx={{ minWidth: 'auto', padding: '8px' }}
                            >
                              <CloseIcon
                                fontSize='small'
                                sx={{ color: 'text.secondary' }}
                              />
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Button
                    onClick={() =>
                      append({
                        role: '' as DefaultRole,
                        relation: '' as CompanyUsersRelation,
                        email: ''
                      })
                    }
                    variant='text'
                    sx={{ color: 'primary.main', textTransform: 'none' }}
                    startIcon={<AddIcon color='primary' />}
                  >
                    {t('create_company.members.add_new_member')}
                  </Button>
                </Box>
                {(hasInvalidEmail || hasInvalidDomain) && (
                  <Stack direction='row' alignItems='center' spacing='.5rem'>
                    <ErrorOutlinedIcon color='error' />
                    {hasInvalidEmail && (
                      <Typography variant='body2' color='error'>
                        {t('create_company.errors.invalid_email.title')}
                      </Typography>
                    )}
                    {hasInvalidDomain && (
                      <Typography variant='body2' color='error'>
                        {t('create_company.errors.invalid_domain.title')}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Stack direction='row' justifyContent='space-between'>
            <Button
              component={Link}
              to='/companies'
              variant='text'
              sx={{ color: 'text.primary', textTransform: 'none' }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              variant='contained'
              sx={{ textTransform: 'none', width: '14.5rem' }}
              disabled={!isAuthorized || !isValid}
            >
              {t('create_company.create')}
            </Button>
          </Stack>

          <Alert
            severity='error'
            sx={{
              visibility: errorType ? 'visible' : 'hidden',
              width: '100%'
            }}
          >
            <AlertTitle>
              {t(
                errorMessages[errorType as keyof typeof errorMessages]?.title ||
                  ''
              )}
            </AlertTitle>
            {t(
              errorMessages[errorType as keyof typeof errorMessages]
                ?.description || ''
            )}
          </Alert>

          <Dialog open={createCompanyMutation.isPending}>
            <DialogContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '3rem'
              }}
            >
              <CircularProgress />
              <Typography>{t('create_company.loader')}</Typography>
            </DialogContent>
          </Dialog>
        </Box>
      </Container>
    </>
  )
}
